import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, AdminRoute, PublicRoute, RBACRoute } from "./components/ProtectedRoute";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { AppMessageProvider } from "./components/AppMessage";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetupPage from "./pages/SetupPage";

// App Pages
import DailyLogPage from "./pages/DailyLogPage";
import HistoryPage from "./pages/HistoryPage";
import ProfilePage from "./pages/ProfilePage";
import TeamLogsPage from "./pages/TeamLogsPage";
import DashboardPage from "./pages/DashboardPage";
import LeaderboardPage from "./pages/LeaderboardPage";

// Admin Pages
import UsersPage from "./pages/admin/UsersPage";
import ReportsPage from "./pages/admin/ReportsPage";
import ActivityReportPage from "./pages/admin/reports/ActivityReportPage";
import SummaryRecruitersReportPage from "./pages/admin/reports/SummaryRecruitersReportPage";
import TeamsPage from "./pages/admin/TeamsPage";
import AllLogsPage from "./pages/admin/AllLogsPage";
import SettingsPage from "./pages/admin/SettingsPage";
import UserReportPage from "./pages/admin/UserReportPage";
import EmailLogsPage from "./pages/admin/EmailLogsPage";
import IndividualUserReportPage from "./pages/admin/IndividualUserReportPage";
import UserDashboardPage from "./pages/admin/UserDashboardPage";
import FormsPage from "./pages/admin/FormsPage";
import JobDivaMatchingPage from "./pages/admin/JobDivaMatchingPage";

// CRM Pages
import CompaniesPage from "./pages/admin/CompaniesPage";
import CompanyDetailPage from "./pages/admin/CompanyDetailPage";
import LeadsPage from "./pages/admin/LeadsPage";
import LeadDetailPage from "./pages/admin/LeadDetailPage";
import TemplatesPage from "./pages/admin/TemplatesPage";
import TasksPage from "./pages/admin/TasksPage";
import SocialPage from "./pages/admin/SocialPage";
import InboxPage from "./pages/admin/InboxPage";
import AICallCampaignsPage from "./pages/admin/AICallCampaignsPage";
import DialerPage from "./pages/admin/DialerPage";
import CallMonitorPage from "./pages/admin/CallMonitorPage";
import CallDispositionDashboard from "./pages/admin/CallDispositionDashboard";
import SequencesPage from "./pages/admin/SequencesPage";
import EnrichPage from "./pages/admin/EnrichPage";
import MarketingCampaignsPage from "./pages/admin/MarketingCampaignsPage";
import MarketingCampaignDetailPage from "./pages/admin/MarketingCampaignDetailPage";
import RedditIntelligencePage from "./pages/admin/RedditIntelligencePage";
import SeoAuditPage from "./pages/admin/SeoAuditPage";
import LinkedinDataPage from "./pages/admin/LinkedinDataPage";
import LinkedinActivityPage from "./pages/admin/LinkedinActivityPage";
import EmailListPage from "./pages/admin/EmailListPage";
import EmailPersonalizePage from "./pages/admin/EmailPersonalizePage";
import EmailCampaignsPage from "./pages/admin/EmailCampaignsPage";
import MarketingDashboardPage from "./pages/admin/MarketingDashboardPage";
import SocialCalendarPage from "./pages/admin/SocialCalendarPage";

// Franchise Pages
import FranchiseLeadsPage from "./pages/admin/FranchiseLeadsPage";
import FranchiseListsPage from "./pages/admin/FranchiseListsPage";
import FranchiseListDetailPage from "./pages/admin/FranchiseListDetailPage";
import FranchiseTasksPage from "./pages/admin/FranchiseTasksPage";
import FranchiseLeadDetailPage from "./pages/admin/FranchiseLeadDetailPage";

// Document Library
import DocumentsPage from "./pages/admin/DocumentsPage";

// Knowledge Base
import KnowledgeBasePage from "./pages/admin/KnowledgeBasePage";

// Email Verification
import EmailVerifyPage from "./pages/EmailVerifyPage";

// Layout
import DashboardLayout from "./components/DashboardLayout";

import "./App.css";

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (<div style={{padding:40,fontFamily:'monospace'}}>
        <h2 style={{color:'red'}}>React Crash</h2>
        <pre style={{whiteSpace:'pre-wrap',background:'#f5f5f5',padding:16}}>{this.state.error.message}{'\n\n'}{this.state.error.stack}</pre>
        <button onClick={() => { this.setState({error:null}); window.location.reload(); }}>Reload</button>
      </div>);
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
    <AppMessageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/setup" element={<SetupPage />} />

          {/* Single DashboardLayout instance for all authenticated routes — prevents
              sidebar nav from remounting (and briefly disappearing) on navigation */}
          <Route element={<ProtectedRoute><PermissionsProvider><DashboardLayout /></PermissionsProvider></ProtectedRoute>}>

            {/* User routes */}
            <Route path="/app/log" element={<DailyLogPage />} />
            <Route path="/app/history" element={<HistoryPage />} />
            <Route path="/app/profile" element={<ProfilePage />} />
            <Route path="/app/team-logs" element={<RBACRoute menuKey="team_management"><TeamLogsPage /></RBACRoute>} />
            <Route path="/app/leaderboard" element={<LeaderboardPage />} />

            {/* Dashboard */}
            <Route path="/app/dashboard" element={<RBACRoute menuKey="dashboard"><DashboardPage /></RBACRoute>} />

            {/* Team Management routes */}
            <Route path="/admin/logs" element={<RBACRoute menuKey="team_management"><AllLogsPage /></RBACRoute>} />
            <Route path="/admin/reports" element={<RBACRoute menuKey="team_management"><ReportsPage /></RBACRoute>} />
            <Route path="/admin/reports/activity" element={<AdminRoute><ActivityReportPage /></AdminRoute>} />
            <Route path="/admin/reports/summary-recruiters" element={<RBACRoute menuKey="team_management"><SummaryRecruitersReportPage /></RBACRoute>} />
            <Route path="/admin/user-report" element={<RBACRoute menuKey="team_management"><IndividualUserReportPage /></RBACRoute>} />
            <Route path="/admin/user-dashboard" element={<RBACRoute menuKey="team_management"><UserDashboardPage /></RBACRoute>} />

            {/* CRM routes */}
            <Route path="/admin/companies" element={<RBACRoute menuKey="crm_companies"><CompaniesPage /></RBACRoute>} />
            <Route path="/admin/companies/:id" element={<RBACRoute menuKey="crm_companies"><CompanyDetailPage /></RBACRoute>} />
            <Route path="/admin/leads" element={<RBACRoute menuKey="crm_leads"><LeadsPage /></RBACRoute>} />
            <Route path="/admin/leads/:id" element={<RBACRoute menuKey="crm_leads"><LeadDetailPage /></RBACRoute>} />
            <Route path="/admin/tasks" element={<RBACRoute menuKey="crm_tasks"><TasksPage /></RBACRoute>} />
            <Route path="/admin/inbox" element={<RBACRoute menuKey="crm_inbox"><InboxPage /></RBACRoute>} />
            <Route path="/admin/enrich" element={<RBACRoute menuKey="crm_leads"><EnrichPage /></RBACRoute>} />
            <Route path="/admin/email-verify" element={<RBACRoute menuKey="crm_leads"><EmailVerifyPage /></RBACRoute>} />
            <Route path="/admin/templates" element={<RBACRoute menuKey="templates"><TemplatesPage /></RBACRoute>} />
            <Route path="/admin/social" element={<RBACRoute menuKey="social"><SocialPage /></RBACRoute>} />

            {/* Marketing routes */}
            <Route path="/admin/marketing" element={<RBACRoute menuKey="marketing"><MarketingCampaignsPage /></RBACRoute>} />
            <Route path="/admin/marketing/campaigns/:campaignId" element={<RBACRoute menuKey="marketing"><MarketingCampaignDetailPage /></RBACRoute>} />
            <Route path="/admin/marketing/reddit" element={<RBACRoute menuKey="marketing"><RedditIntelligencePage /></RBACRoute>} />
            <Route path="/admin/marketing/seo-audit" element={<RBACRoute menuKey="marketing"><SeoAuditPage /></RBACRoute>} />
            <Route path="/admin/marketing/linkedin-data" element={<RBACRoute menuKey="marketing"><LinkedinDataPage /></RBACRoute>} />
            <Route path="/admin/marketing/linkedin-activity" element={<RBACRoute menuKey="marketing"><LinkedinActivityPage /></RBACRoute>} />
            <Route path="/admin/marketing/email-list" element={<RBACRoute menuKey="marketing"><EmailListPage /></RBACRoute>} />
            <Route path="/admin/marketing/email-personalize" element={<RBACRoute menuKey="marketing"><EmailPersonalizePage /></RBACRoute>} />
            <Route path="/admin/marketing/email-campaigns" element={<RBACRoute menuKey="marketing"><EmailCampaignsPage /></RBACRoute>} />
            <Route path="/admin/marketing/dashboard" element={<RBACRoute menuKey="marketing"><MarketingDashboardPage /></RBACRoute>} />
            <Route path="/admin/marketing/social-calendar" element={<RBACRoute menuKey="marketing"><SocialCalendarPage /></RBACRoute>} />


            {/* Franchise routes */}
            <Route path="/admin/lists" element={<RBACRoute menuKey="franchise_lists"><FranchiseListsPage /></RBACRoute>} />
            <Route path="/admin/lists/:id" element={<RBACRoute menuKey="franchise_lists"><FranchiseListDetailPage /></RBACRoute>} />
            <Route path="/admin/franchise/leads" element={<RBACRoute menuKey="franchise_leads"><FranchiseLeadsPage /></RBACRoute>} />
            <Route path="/admin/franchise/leads/:id" element={<RBACRoute menuKey="franchise_leads"><FranchiseLeadDetailPage /></RBACRoute>} />
            <Route path="/admin/franchise/tasks" element={<RBACRoute menuKey="franchise_tasks"><FranchiseTasksPage /></RBACRoute>} />

            {/* AI & Calls routes */}
            <Route path="/admin/ai-calls" element={<RBACRoute menuKey="ai_calls"><AICallCampaignsPage /></RBACRoute>} />
            <Route path="/admin/dialer" element={<RBACRoute menuKey="ai_calls"><DialerPage /></RBACRoute>} />
            {/* Legacy live-session URL → just send users to the unified dialer page */}
            <Route path="/admin/ai-calls/live/:campaignId" element={<Navigate to="/admin/dialer" replace />} />
            <Route path="/admin/call-monitor" element={<RBACRoute menuKey="call_monitor"><CallMonitorPage /></RBACRoute>} />
            <Route path="/admin/call-dashboard" element={<RBACRoute menuKey="ai_calls"><CallDispositionDashboard /></RBACRoute>} />
            <Route path="/admin/sequences" element={<RBACRoute menuKey="ai_calls"><SequencesPage /></RBACRoute>} />

            {/* Open to all authenticated users */}
            <Route path="/admin/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/documents" element={<RBACRoute menuKey="documents"><DocumentsPage /></RBACRoute>} />

            {/* Admin routes */}
            <Route path="/admin/users" element={<RBACRoute menuKey="admin_users"><UsersPage /></RBACRoute>} />
            <Route path="/admin/teams" element={<RBACRoute menuKey="admin_teams"><TeamsPage /></RBACRoute>} />
            <Route path="/admin/settings" element={<RBACRoute menuKey="admin_settings"><SettingsPage /></RBACRoute>} />
            <Route path="/admin/jobdiva-matching" element={<AdminRoute><JobDivaMatchingPage /></AdminRoute>} />
            <Route path="/admin/email-logs" element={<RBACRoute menuKey="admin_users"><EmailLogsPage /></RBACRoute>} />
            <Route path="/admin/forms" element={<AdminRoute><FormsPage /></AdminRoute>} />

          </Route>

          {/* User Report - renders without the layout sidebar */}
          <Route path="/admin/user-report/:userId" element={<RBACRoute menuKey="team_management"><UserReportPage /></RBACRoute>} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/app/log" replace />} />
          <Route path="*" element={<Navigate to="/app/log" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </AppMessageProvider>
    </ErrorBoundary>
  );
}

export default App;
